import type { User, Flight, Overlap, OverlapFriend } from '../types';
import { buildRoster } from './roster';
import { dateRangeOverlap, daysBetween } from './dates';
import { getStayPeriods } from './overlapGroups';

/**
 * Calculate overlaps between the current user and their friends.
 * Uses the unified getStayPeriods() for both coral-dot and Gantt-card paths.
 */
export function calculateOverlaps(
  currentUser: User,
  currentUserFlights: Flight[],
  friends: User[],
  allFlights: Flight[],
  month: number,
  year: number
): Overlap[] {
  if (friends.length === 0) return [];

  // Build rosters and extract stay periods
  const myRoster = buildRoster(currentUserFlights, currentUser, month, year);
  const myPeriods = getStayPeriods(myRoster, currentUser.baseAirport);

  // Build friend periods
  const friendPeriods: Array<{ city: string; startDate: string; endDate: string; reason: 'layover' | 'free_at_base'; user: User }> = [];
  for (const friend of friends) {
    const friendFlights = allFlights.filter((f) => f.userId === friend.id);
    const friendRoster = buildRoster(friendFlights, friend, month, year);
    for (const sp of getStayPeriods(friendRoster, friend.baseAirport)) {
      friendPeriods.push({ ...sp, user: friend });
    }
  }

  // Find overlaps: for each of my periods, check against friend periods
  const rawOverlaps: Array<{
    city: string;
    start: string;
    end: string;
    friend: OverlapFriend;
  }> = [];

  for (const myPeriod of myPeriods) {
    for (const friendPeriod of friendPeriods) {
      if (myPeriod.city !== friendPeriod.city) continue;

      const overlap = dateRangeOverlap(
        myPeriod.startDate,
        myPeriod.endDate,
        friendPeriod.startDate,
        friendPeriod.endDate
      );

      if (overlap) {
        rawOverlaps.push({
          city: myPeriod.city,
          start: overlap.start,
          end: overlap.end,
          friend: {
            user: friendPeriod.user,
            reason: friendPeriod.reason,
          },
        });
      }
    }
  }

  // Merge overlaps: group by city + overlapping date ranges, collect friends
  const merged: Overlap[] = [];

  for (const raw of rawOverlaps) {
    const existing = merged.find(
      (o) => o.city === raw.city && o.startDate === raw.start && o.endDate === raw.end
    );

    if (existing) {
      if (!existing.friends.some((f) => f.user.id === raw.friend.user.id)) {
        existing.friends.push(raw.friend);
      }
    } else {
      merged.push({
        id: `overlap-${raw.city}-${raw.start}-${raw.end}`,
        city: raw.city,
        startDate: raw.start,
        endDate: raw.end,
        days: daysBetween(raw.start, raw.end),
        friends: [raw.friend],
      });
    }
  }

  merged.sort((a, b) => a.startDate.localeCompare(b.startDate));

  return merged;
}

/**
 * Get a count of friends in a given city on a given date.
 */
export function getFriendsInCity(
  city: string,
  date: string,
  overlaps: Overlap[]
): OverlapFriend[] {
  const matching: OverlapFriend[] = [];
  for (const overlap of overlaps) {
    if (overlap.city === city && date >= overlap.startDate && date <= overlap.endDate) {
      matching.push(...overlap.friends);
    }
  }
  // Deduplicate by user id
  const seen = new Set<string>();
  return matching.filter((f) => {
    if (seen.has(f.user.id)) return false;
    seen.add(f.user.id);
    return true;
  });
}
